/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import VmItemForm from '@web/src/features/vendor-market/vm-item-form/vm-item-form'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { VendorMarketItemInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  EditVendorMarketItemQuery,
  EditVendorMarketItemQueryVariables,
  UpdateVendorMarketItemInput,
  UpdateVendorMarketItemMutation,
  UpdateVendorMarketItemMutationVariables,
} from 'types/graphql'

const EDIT_ITEM_QUERY: TypedDocumentNode<EditVendorMarketItemQuery, EditVendorMarketItemQueryVariables> = gql`
  query EditVendorMarketItem($id: String!) {
    vendorMarketItem(id: $id) {
      ...VendorMarketItemInfo
    }
  }
  ${VendorMarketItemInfo}
`

const UPDATE_ITEM_MUTATION: TypedDocumentNode<UpdateVendorMarketItemMutation, UpdateVendorMarketItemMutationVariables> =
  gql`
  mutation UpdateVendorMarketItem($id: String!, $input: UpdateVendorMarketItemInput!) {
    updateVendorMarketItem(id: $id, input: $input) {
      ...VendorMarketItemInfo
    }
  }
  ${VendorMarketItemInfo}
`

export const Route = createFileRoute('/(auth)/(main-layout)/vendor-market/$vendorMarketIdOrSlug_/item/$itemId_/edit')({
  component: EditItemPage,
  head: () =>
    seo({
      title: `Edit Item | ${env.VITE_APP_NAME}`,
      description: 'Edit a vendor item',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, EDIT_ITEM_QUERY, {
      variables: { id: params.itemId },
    })
    return { queryRef }
  },
})

function EditItemPage() {
  const { t } = useTranslation()
  const { vendorMarketIdOrSlug, itemId } = Route.useParams()
  const [showSpinner, setShowSpinner] = useState(false)
  const navigate = useNavigate()
  const { queryRef } = Route.useLoaderData()
  const { data, error } = useReadQuery(queryRef as QueryRef<EditVendorMarketItemQuery>)

  const [updateItem] = useMutation(UPDATE_ITEM_MUTATION, {
    onCompleted: () => {
      toast.success(t('Item Updated'))
      navigate({
        to: '/vendor-market/$vendorMarketIdOrSlug/item/$itemId',
        params: { vendorMarketIdOrSlug, itemId } as never,
      })
      setShowSpinner(false)
    },
    onError: (err) => {
      toast.error(err.message)
      setShowSpinner(false)
    },
  })

  const onSave = (input: UpdateVendorMarketItemInput) => {
    setShowSpinner(true)
    updateItem({ variables: { id: itemId, input } })
  }

  if (error) return <ErrorAlert error={error} />
  if (!data?.vendorMarketItem) return <ErrorAlert error={new Error(t('Item not found'))} />

  return (
    <div className="mx-auto w-full max-w-2xl pt-4">
      <div className="flex w-full flex-col items-stretch gap-4">
        <h1 className="w-full text-center font-bold text-4xl">{t('Edit Item')}</h1>
        <VmItemForm
          item={data.vendorMarketItem}
          onSave={onSave}
          showSpinner={showSpinner}
          vendorMarketId={vendorMarketIdOrSlug}
        />
      </div>
    </div>
  )
}
